import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { wishlistService } from '../services/wishlist.service';
import {
  setWishlist,
  setWishlistCount,
  addItem,
  removeItem,
  clearWishlist as clearWishlistAction,
  setLoading,
  setError,
  selectWishlist,
  selectWishlistItems,
  selectWishlistCount,
} from '../store/slices/wishlistSlice';
import { useToast } from '../components/common/Toast';

export const useWishlist = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const wishlist = useSelector(selectWishlist);
  const items = useSelector(selectWishlistItems);
  const count = useSelector(selectWishlistCount);

  const fetchWishlist = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const response = await wishlistService.getWishlist();
      dispatch(setWishlist(response.data));
      return response.data;
    } catch (error) {
      if (error.response?.status !== 401) {
        const errorMessage = error.response?.data?.message || 'Failed to load wishlist';
        dispatch(setError(errorMessage));
      }
      dispatch(setLoading(false));
      throw error;
    }
  }, [dispatch]);

  const fetchCount = useCallback(async () => {
    try {
      const response = await wishlistService.getWishlistCount();
      dispatch(setWishlistCount(response.data.count));
    } catch (error) {
      console.error('Failed to fetch wishlist count');
    }
  }, [dispatch]);

  const addToWishlist = useCallback(async (productId) => {
    dispatch(setLoading(true));
    try {
      const response = await wishlistService.addToWishlist(productId);
      dispatch(addItem(response.data.wishlistItem));
      toast.success('Added to wishlist');
      await fetchCount();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add to wishlist';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, toast, fetchCount]);

  const removeFromWishlist = useCallback(async (productId) => {
    dispatch(setLoading(true));
    try {
      await wishlistService.removeFromWishlist(productId);
      dispatch(removeItem(productId));
      toast.success('Removed from wishlist');
      await fetchCount();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to remove from wishlist';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, toast, fetchCount]);

  const toggleWishlist = useCallback(async (productId, isInWishlist) => {
    if (isInWishlist) {
      await removeFromWishlist(productId);
    } else {
      await addToWishlist(productId);
    }
  }, [addToWishlist, removeFromWishlist]);

  const clearWishlist = useCallback(async () => {
    if (!window.confirm('Are you sure you want to clear your wishlist?')) return;
    
    dispatch(setLoading(true));
    try {
      await wishlistService.clearWishlist();
      dispatch(clearWishlistAction());
      toast.success('Wishlist cleared');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to clear wishlist';
      dispatch(setError(errorMessage));
      toast.error(errorMessage);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, toast]);

  const isInWishlist = useCallback((productId) => {
    return items.some(item => item.product_id === productId);
  }, [items]);

  return {
    items,
    count,
    loading: wishlist.loading,
    error: wishlist.error,
    fetchWishlist,
    fetchCount,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    clearWishlist,
    isInWishlist,
  };
};

export default useWishlist;